import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { useAppContext } from "../context/AppContext";
import { apiFetch, queryString } from "../lib/api";
import type { Paginated } from "../shared/schemas";
import { ErrorState, LoadingState, PageHeader, StatusBadge } from "../components/ui";

interface ComparisonRow {
  crn: string;
  changeType: "added" | "removed" | "changed" | "unchanged";
  before: Record<string, string | number | null>;
  after: Record<string, string | number | null>;
}

interface ComparisonResponse extends Paginated<ComparisonRow> {
  metrics: {
    delta: {
      totalSections: number;
      totalEnrollment: number;
      activeRooms: number;
      averageRoomUtilization: number;
      conflictCount: number;
      bottleneckCount: number;
    };
  };
}

export function ComparisonsPage() {
  const { terms, snapshotTermId } = useAppContext();
  const [before, setBefore] = useState("");
  const [after, setAfter] = useState(snapshotTermId);
  const [changeType, setChangeType] = useState("");
  useEffect(() => setAfter(snapshotTermId), [snapshotTermId]);
  useEffect(() => {
    if (!before && terms.length > 1) setBefore(terms.find((term) => term.id !== after)?.id ?? "");
  }, [after, before, terms]);
  const query = useQuery({
    queryKey: ["comparison", before, after, changeType],
    queryFn: () =>
      apiFetch<ComparisonResponse>(
        `/comparisons${queryString({ before, after, changeType, pageSize: 100 })}`,
      ),
    enabled: Boolean(before && after && before !== after),
  });
  return (
    <>
      <PageHeader title="Historical Comparison" description="Compare immutable snapshots by CRN and normalized meeting pattern." />
      <div className="panel grid gap-4 p-5 md:grid-cols-[1fr_auto_1fr_180px] md:items-end">
        <label><span className="mb-1 block text-xs font-bold uppercase text-slate-500">Before</span><select className="field" value={before} onChange={(event) => setBefore(event.target.value)}><option value="">Select snapshot</option>{terms.map((term) => <option key={term.id} value={term.id}>{term.displayName} / {term.termCode} · {new Date(term.createdAt).toLocaleDateString()}</option>)}</select></label>
        <ArrowRight className="mb-2 hidden h-5 w-5 text-slate-400 md:block" />
        <label><span className="mb-1 block text-xs font-bold uppercase text-slate-500">After</span><select className="field" value={after} onChange={(event) => setAfter(event.target.value)}><option value="">Select snapshot</option>{terms.map((term) => <option key={term.id} value={term.id}>{term.displayName} / {term.termCode} · {new Date(term.createdAt).toLocaleDateString()}</option>)}</select></label>
        <label><span className="mb-1 block text-xs font-bold uppercase text-slate-500">Change</span><select className="field" value={changeType} onChange={(event) => setChangeType(event.target.value)}><option value="">All changes</option><option value="added">Added</option><option value="removed">Removed</option><option value="changed">Changed</option><option value="unchanged">Unchanged</option></select></label>
      </div>
      {query.isLoading && <LoadingState label="Comparing snapshots..." />}
      {query.error && <ErrorState error={query.error} />}
      {query.data && (
        <>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
          {[
            ["Sections", query.data.metrics.delta.totalSections],
            ["Enrollment", query.data.metrics.delta.totalEnrollment],
            ["Rooms", query.data.metrics.delta.activeRooms],
            ["Utilization", `${(query.data.metrics.delta.averageRoomUtilization * 100).toFixed(1)} pts`],
            ["Conflicts", query.data.metrics.delta.conflictCount],
            ["Bottlenecks", query.data.metrics.delta.bottleneckCount],
          ].map(([label, value]) => (
            <div key={label} className="panel p-4">
              <p className="text-[10px] font-bold uppercase text-slate-400">{label}</p>
              <p className="mt-1 text-xl font-black text-slate-900">{typeof value === "number" && value > 0 ? `+${value}` : value}</p>
            </div>
          ))}
        </div>
        <div className="panel overflow-hidden">
          <div className="border-b border-slate-200 px-5 py-3 text-xs text-slate-500">{query.data.total.toLocaleString()} section deltas</div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[950px] text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">CRN</th><th className="px-4 py-3">Change</th><th className="px-4 py-3">Before</th><th className="px-4 py-3">After</th><th className="px-4 py-3">Enrollment Δ</th><th className="px-4 py-3">Max Δ</th><th className="px-4 py-3">Meeting</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {query.data.items.map((row) => (
                  <tr key={row.crn}>
                    <td className="px-4 py-3 font-mono font-bold">{row.crn}</td>
                    <td className="px-4 py-3"><StatusBadge status={row.changeType} /></td>
                    <td className="px-4 py-3">{String(row.before.course ?? "—")}</td>
                    <td className="px-4 py-3">{String(row.after.course ?? "—")}</td>
                    <td className="px-4 py-3">{Number(row.after.actualEnrollment ?? 0) - Number(row.before.actualEnrollment ?? 0)}</td>
                    <td className="px-4 py-3">{Number(row.after.maxEnrollment ?? 0) - Number(row.before.maxEnrollment ?? 0)}</td>
                    <td className="max-w-xs truncate px-4 py-3 text-xs text-slate-500">{row.before.meetings === row.after.meetings ? "Unchanged" : "Changed"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}
    </>
  );
}
